"use client";

import { Advertisement } from "@/services/AdvertisementService";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface AdvertisementPreviewProps {
  advertisement: Advertisement;
  onClose: () => void;
}

export default function AdvertisementPreview({
  advertisement,
  onClose,
}: AdvertisementPreviewProps) {
  // Replace placeholders in text
  const renderText = (text: string) => {
    return text
      .replace(/\[name\]/g, "John Doe")
      .replace(/\[offerurl\]/g, "https://example.com/offer")
      .replace(/\${accesstoken}/g, "sample-access-token");
  };

  const renderButtonUrl = (url: string) => {
    return url
      .replace(/\[offerurl\]/g, "https://example.com/offer")
      .replace(/\[name\]/g, "John Doe")
      .replace(/\${accesstoken}/g, "sample-access-token");
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            Advertisement Preview
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Ad Info */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Ad ID:</span>{" "}
                <span className="text-gray-900">#{advertisement.adid}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Type:</span>{" "}
                <span className="text-gray-900">
                  {advertisement.type === "ad_vid" ? "Video" : "Photo"}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>{" "}
                <span
                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    advertisement.active
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {advertisement.active ? "Active" : "Inactive"}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Pages:</span>{" "}
                <span className="text-gray-900">
                  {advertisement.profilepage != null && `Profile (p${advertisement.profilepage})`}
                  {advertisement.profilepage != null && advertisement.sessionpage != null && ", "}
                  {advertisement.sessionpage != null && `Session (p${advertisement.sessionpage})`}
                  {advertisement.profilepage == null && advertisement.sessionpage == null && "No placement"}
                </span>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Mobile App Preview
              </h4>
              <div className="bg-gray-100 rounded-lg p-4 max-w-md mx-auto">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  {/* Media */}
                  {advertisement.type === "ad_photo" ? (
                    <img
                      src={advertisement.resource}
                      alt="Advertisement"
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "https://via.placeholder.com/400x200?text=Image+Not+Found";
                      }}
                    />
                  ) : (
                    <div className="relative w-full h-48 bg-black">
                      {advertisement.resource.includes("youtube.com") ||
                      advertisement.resource.includes("youtu.be") ? (
                        <iframe
                          src={advertisement.resource}
                          title="Advertisement Video"
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : advertisement.resource.includes(".html") ? (
                        <iframe
                          src={advertisement.resource}
                          title="Advertisement Video"
                          className="w-full h-full"
                          sandbox="allow-scripts allow-same-origin"
                        />
                      ) : (
                        <video
                          src={advertisement.resource}
                          controls
                          className="w-full h-full"
                        >
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-4">
                    <p className="text-gray-800 text-sm mb-3">
                      {renderText(advertisement.adtext)}
                    </p>
                    <a
                      href={renderButtonUrl(advertisement.buttonurl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      {advertisement.buttontext}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Details */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Technical Details
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Resource URL:
                  </label>
                  <div className="text-xs text-gray-600 break-all bg-white p-2 rounded border border-gray-200">
                    {advertisement.resource}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Share URL:
                  </label>
                  <div className="text-xs text-gray-600 break-all bg-white p-2 rounded border border-gray-200">
                    {advertisement.shareurl}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Button URL:
                  </label>
                  <div className="text-xs text-gray-600 break-all bg-white p-2 rounded border border-gray-200">
                    {advertisement.buttonurl}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Qualifying Query:
                  </label>
                  <pre className="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                    {advertisement.qualifyingquery}
                  </pre>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Profile Page Priority:
                    </label>
                    <div className="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200">
                      {advertisement.profilepage || "Disabled"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Session Page Priority:
                    </label>
                    <div className="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200">
                      {advertisement.sessionpage || "Disabled"}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Internal URL:
                    </label>
                    <div className="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200">
                      {advertisement.internalurl ? "Yes" : "No"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      List Positions:
                    </label>
                    <div className="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200">
                      Profile: {advertisement.profilelistposition}, Session:{" "}
                      {advertisement.sessionlistposition}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}